import { Brand, ESLockbox, LockboxType } from '@bb/domain/dist/types';
import { getLockbox } from 'api/get';
import axios from 'axios';
import React, { createContext, useContext, useState } from 'react';
import { message } from 'components';
import { useParams } from 'react-router-dom';
import swal from 'sweetalert2';
import { debounce } from 'lodash';
import errorReporter from '@app/errorReporter';
import { deleteLockbox as deleteLockboxRequest } from 'api/LockboxController';
import { TableSortType } from '../MLSStaffPages/types';
import { useSearchParams } from '../AnsweringSearchPage/hooks';

const NEW_LOCKBOX_REFETCH_DELAY = 2500;

type LockboxesContextType = {
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  total: number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  initialTotal: number;
  setInitialTotal: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  lockboxes: ESLockbox[];
  setLockboxes: React.Dispatch<React.SetStateAction<ESLockbox[]>>;
  currentLockbox: ESLockbox | null | undefined;
  setCurrentLockbox: React.Dispatch<React.SetStateAction<ESLockbox | null>>;
  sort: TableSortType;
  setSort: React.Dispatch<React.SetStateAction<TableSortType>>;
  showTable: boolean;
  setShowTable: React.Dispatch<React.SetStateAction<boolean>>;
  brandsFilter: Brand[];
  setBrandsFilter: React.Dispatch<React.SetStateAction<Brand[]>>;
  lockboxTypeFilter: LockboxType[];
  setLockboxTypeFilter: React.Dispatch<React.SetStateAction<LockboxType[]>>;
  selectedOwners: Record<string, NonNullable<ESLockbox['owner']>>;
  setSelectedOwners: React.Dispatch<
    React.SetStateAction<Record<string, NonNullable<ESLockbox['owner']>>>
  >;
  selectedAssignee: Record<string, NonNullable<ESLockbox['assignee']>>;
  setSelectedAssignee: React.Dispatch<
    React.SetStateAction<Record<string, NonNullable<ESLockbox['assignee']>>>
  >;
  selectedListings: Record<string, NonNullable<ESLockbox['listing']>>;
  setSelectedListings: React.Dispatch<
    Record<string, NonNullable<ESLockbox['listing']>>
  >;
  deleteLockbox: (lockbox: ESLockbox) => Promise<void>;
  selectedLockboxes: string[];
  setSelectedLockboxes: React.Dispatch<React.SetStateAction<string[]>>;
  onLockboxAdded: () => void;
} | null;

const LockboxesContext = createContext<LockboxesContextType>(null);

export const useLockboxesContext = () => {
  const lockboxesContext = useContext(LockboxesContext);

  if (!lockboxesContext) {
    throw new Error(
      'useLockboxesContext must be used within a LockboxesContextProvider',
    );
  }

  return lockboxesContext;
};

const LockboxesContextProvider: React.FC = ({ children }) => {
  const { page: initialPage } = useParams<{ page?: string }>();
  const [text, setText] = React.useState('');
  const [page, setPage] = React.useState(parseInt(initialPage ?? '1', 10));
  const [total, setTotal] = React.useState(0);
  const [initialTotal, setInitialTotal] = React.useState(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showTable, setShowTable] = React.useState(true);
  const [lockboxes, setLockboxes] = React.useState<ESLockbox[]>([]);
  const [currentLockbox, setCurrentLockbox] =
    React.useState<ESLockbox | null>();
  const [searchParam, setSearchParam] = useSearchParams<{
    sort: TableSortType;
  }>();
  const [sort, setSort] = React.useState<TableSortType>({
    ...searchParam.sort,
  });

  // #region Filter states
  const [brandsFilter, setBrandsFilter] = React.useState<Brand[]>([]);
  const [lockboxTypeFilter, setLockboxTypeFilter] = React.useState<
    LockboxType[]
  >([]);
  const [selectedListings, setSelectedListings] = React.useState<
    Record<string, NonNullable<ESLockbox['listing']>>
  >({});
  // #endregion

  const [selectedLockboxes, setSelectedLockboxes] = React.useState([]);

  const [selectedOwners, setSelectedOwners] = useState<
    Record<string, NonNullable<ESLockbox['owner']>>
  >({});
  const [selectedAssignee, setSelectedAssignee] = useState<
    Record<string, NonNullable<ESLockbox['owner']>>
  >({});

  const searchDeps: React.DependencyList = [
    sort,
    text,
    brandsFilter,
    lockboxTypeFilter,
    selectedListings,
    selectedAssignee,
    selectedOwners,
  ];

  const LOCKBOX_SEARCH_QUERY = {
    searchTerm: text,
    brands: brandsFilter,
    lockboxTypes: lockboxTypeFilter,
    listings: Object.keys(selectedListings),
    owners: Object.keys(selectedOwners),
    assignees: Object.keys(selectedAssignee),
    sortField: sort?.field ?? 'serialNumber',
    sortOrder: sort?.direction ?? 'asc',
    page,
    size: 20,
  };

  const fetchLockboxes = async ({ delay }: { delay?: number } = {}) => {
    setLoading(true);

    // Delay is necessary after adding a new lockbox to give time for ES index to update
    if (delay) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    const lockboxResult = await getLockbox(LOCKBOX_SEARCH_QUERY);
    setLoading(false);
    setLockboxes(lockboxResult.lockboxes);
    setTotal(lockboxResult.lockboxCount);
    // @ts-expect-error - totalLockboxCount is missing from the type
    setInitialTotal(lockboxResult.totalLockboxCount);
  };

  const debouncedFetchLockboxes = React.useCallback(
    debounce(fetchLockboxes, 500),
    [...searchDeps, page],
  );

  React.useEffect(() => {
    debouncedFetchLockboxes();
  }, [...searchDeps, page]);

  const onLockboxAdded = () => {
    setShowTable(true);
    debouncedFetchLockboxes({ delay: NEW_LOCKBOX_REFETCH_DELAY });
  };

  const deleteLockbox = async (lockbox: ESLockbox) => {
    const { value } = await swal.fire({
      title: 'Delete Lockbox',
      text: 'Click "Confirm" to delete lockbox',
      type: 'error',
      cancelButtonColor: '#3085d6',
      confirmButtonColor: '#d33',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    });
    if (value) {
      try {
        setLoading(true);
        await deleteLockboxRequest(lockbox._id);

        setLockboxes(lockboxes?.filter(lb => lb._id !== lockbox._id));

        message.success('Lockbox has been successfully deleted.');
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <LockboxesContext.Provider
      value={{
        text,
        page,
        total,
        initialTotal,
        loading,
        lockboxes,
        currentLockbox,
        showTable,
        sort,
        setSort,
        setInitialTotal,
        setText,
        setPage,
        setTotal,
        setLockboxes,
        setCurrentLockbox,
        setLoading,
        setShowTable,
        brandsFilter,
        setBrandsFilter,
        lockboxTypeFilter,
        setLockboxTypeFilter,
        selectedListings,
        setSelectedListings,
        selectedOwners,
        setSelectedOwners,
        selectedAssignee,
        setSelectedAssignee,
        deleteLockbox,
        selectedLockboxes,
        setSelectedLockboxes,
        onLockboxAdded,
      }}
    >
      {children}
    </LockboxesContext.Provider>
  );
};

export default LockboxesContextProvider;
